import './core/i18n/i18n.ts';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import './tailwind.css';
import { MsalProvider } from '@azure/msal-react';
import publicClientApplication from './core/msal/publicClientApplication.ts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { routeTree } from './routeTree.gen';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
} from '@azure/msal-browser';

const queryClient = new QueryClient();
const msalInstance = publicClientApplication;

// Create a new router instance
export const router = createRouter({
  routeTree,
  context: {
    queryClient,
    msalInstance,
  },
  scrollRestoration: true,
  getScrollRestorationKey: (location) => location.pathname,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    tabIndex?: number;
  }
}

const rootElement = document.getElementById('root')!;

/**
 * Boots the SPA after MSAL is initialized so auth routes do not stay in an
 * indeterminate startup state on first load.
 */
const startApplication = async (): Promise<void> => {
  await msalInstance.initialize();

  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const authResult = event.payload as AuthenticationResult;
      const account = authResult.account ?? msalInstance.getAllAccounts()[0];

      if (account) {
        msalInstance.setActiveAccount(account);
      } else {
        console.warn(
          'LOGIN_SUCCESS event did not expose an account and no cached account was available.'
        );
      }
    } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
      console.log('Logout success event received.');
    }
  });

  // Some deployed index.html variants inject the entry script into #root.
  // Clear any non-React placeholder content so the app always mounts.
  rootElement.replaceChildren();

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools />
          <RouterProvider router={router} />
        </QueryClientProvider>
      </MsalProvider>
    </StrictMode>
  );
};

void startApplication().catch((error) => {
  console.error('Failed to initialize the frontend application.', error);
  rootElement.replaceChildren();
  rootElement.textContent = 'Frontend initialization failed.';
});
